import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MaskedInput from '../common/MaskedInput';
import { isAuthenticated } from '../api/auth';
import client from '../api/client';
import { useTranslation } from '../context/TranslationContext';
import Container from '../ui/Container';
import PageHeader from '../ui/PageHeader';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import Alert from '../ui/Alert';
import styles from './ApiKey.module.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.fis4j.okankonur.com';

const ENDPOINT_KEYS = [
  { method: 'GET',  path: '/api/receipts',               descKey: 'api.epListReceipts' },
  { method: 'GET',  path: '/api/receipts/{id}',           descKey: 'api.epGetReceipt' },
  { method: 'GET',  path: '/api/receipts/{id}/items',     descKey: 'api.epGetItems' },
  { method: 'POST', path: '/api/receipts/process',        descKey: 'api.epProcess' },
  { method: 'GET',  path: '/api/reports/dashboard-stats', descKey: 'api.epDashStats' },
];

export default function ApiKey() {
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await client.get('/api/users/me', { withCredentials: true });
        if (!cancelled) setApiKey(res.data.apiKey);
      } catch {
        if (!cancelled) setError(t('fetchError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, t]);

  const copy = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Container>
        <div style={{ padding: 'var(--space-9) 0', display: 'flex', justifyContent: 'center' }}>
          <Spinner />
        </div>
      </Container>
    );
  }

  const C = ({ children }) => <code className={styles.inlineCode}>{children}</code>;

  return (
    <Container>
      <PageHeader title={t('apiKeyManagement')} description={t('keepSecure')} />

      {error && <Alert variant="danger" style={{ marginBottom: 'var(--space-4)' }}>{error}</Alert>}

      {/* ── Your key ── */}
      <Card className={styles.keyCard}>
        <Card.Header>
          <Card.Title>{t('yourApiKey')}</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className={styles.keyRow}>
            <MaskedInput type="password" value={apiKey || t('noApiKey')} readOnly />
            <div className={styles.keyActions}>
              <Button variant="secondary" size="sm" onClick={copy} disabled={!apiKey}>
                {copied ? t('linkCopied') : t('copy')}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className={styles.docs}>

        {/* ── Quick Start ── */}
        <Card>
          <Card.Header>
            <Card.Title>{t('api.quickStart')}</Card.Title>
          </Card.Header>
          <Card.Body>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('api.baseUrl')}</h3>
              <pre className={styles.codeBlock}>{API_BASE}</pre>
            </div>

            <div className={styles.section} style={{ marginTop: 'var(--space-5)' }}>
              <h3 className={styles.sectionTitle}>{t('api.authentication')}</h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', margin: '0 0 var(--space-2)' }}>
                {t('api.authDescPre')} <C>X-API-Key</C> {t('api.authDescPost')}
              </p>
              <pre className={styles.codeBlock}>{
`# curl\ncurl ${API_BASE}/api/receipts \\\n  -H "X-API-Key: YOUR_API_KEY"`
              }</pre>
              <pre className={styles.codeBlock}>{
`// JavaScript (fetch)\nconst res = await fetch('${API_BASE}/api/receipts', {\n  headers: { 'X-API-Key': 'YOUR_API_KEY' }\n});\nconst data = await res.json();`
              }</pre>
            </div>
          </Card.Body>
        </Card>

        {/* ── Endpoints ── */}
        <Card>
          <Card.Header>
            <Card.Title>{t('api.endpoints')}</Card.Title>
          </Card.Header>
          <Card.Body>
            <table className={styles.endpointTable}>
              <thead>
                <tr>
                  <th>{t('api.colMethod')}</th>
                  <th>{t('api.colPath')}</th>
                  <th>{t('api.colDesc')}</th>
                </tr>
              </thead>
              <tbody>
                {ENDPOINT_KEYS.map((ep) => (
                  <tr key={ep.path}>
                    <td>
                      <span className={`${styles.method} ${ep.method === 'GET' ? styles.methodGet : styles.methodPost}`}>
                        {ep.method}
                      </span>
                    </td>
                    <td className={styles.endpointPath}>{ep.path}</td>
                    <td className={styles.endpointDesc}>{t(ep.descKey)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className={styles.section} style={{ marginTop: 'var(--space-5)' }}>
              <h3 className={styles.sectionTitle}>{t('api.uploadCurl')}</h3>
              <pre className={styles.codeBlock}>{
`curl -X POST ${API_BASE}/api/receipts/process \\\n  -H "X-API-Key: YOUR_API_KEY" \\\n  -F "file=@receipt.jpg"`
              }</pre>
            </div>
          </Card.Body>
        </Card>

        {/* ── iOS Shortcuts ── */}
        <Card>
          <Card.Header>
            <Card.Title>{t('api.iosShortcuts')}</Card.Title>
          </Card.Header>
          <Card.Body>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', margin: '0 0 var(--space-4)' }}>
              {t('api.iosIntro')}
            </p>

            <div className={styles.recipes}>
              {/* Recipe 1: Fetch receipts */}
              <div className={styles.recipe}>
                <p className={styles.recipeTitle}>{t('api.r1Title')}</p>
                <ol className={styles.steps}>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>{t('api.r1Step1')}</span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>{t('api.r1Step2')}</span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>
                      <span>{t('api.r1Step3')}</span>
                      <C>{API_BASE}/api/receipts</C>
                    </span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>
                      <span>{t('api.r1Step4')}</span>
                      <span>{t('api.keyLabel')} <C>X-API-Key</C></span>
                      <span>{t('api.valueLabel')}</span>
                    </span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>{t('api.r1Step5')}</span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>{t('api.r1Step6')}</span>
                  </li>
                </ol>
                <p className={styles.note}>{t('api.r1Note')}</p>
              </div>

              {/* Recipe 2: Upload photo */}
              <div className={styles.recipe}>
                <p className={styles.recipeTitle}>{t('api.r2Title')}</p>
                <ol className={styles.steps}>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>{t('api.r2Step1')}</span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>{t('api.r2Step2')}</span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>
                      <span>{t('api.r2Step3')}</span>
                      <C>{API_BASE}/api/receipts/process</C>
                    </span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>
                      <span>{t('api.r2Step4Pre')} <C>POST</C></span>
                    </span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>
                      <span>{t('api.r2Step5a')}</span>
                      <span>{t('api.r2Step5bPre')} <C>file</C>{t('api.r2Step5bPost')}</span>
                    </span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>
                      <span>{t('api.r2Step6')}</span>
                      <span>{t('api.keyLabel')} <C>X-API-Key</C>{t('api.r2Step6b')}</span>
                    </span>
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepContent}>{t('api.r2Step7')}</span>
                  </li>
                </ol>
                <p className={styles.note}>{t('api.r2Note')}</p>
              </div>
            </div>
          </Card.Body>
        </Card>

      </div>
    </Container>
  );
}
